import React, { useEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { useStore } from '@tanstack/react-form';
import { useCashPools } from '@/hooks/cash-pool';
import { getAgreementType } from '@/types';
import { poolsItsCash } from '@/utils/trades/cash-pool-rate';
import type { TradeFormValues } from '../../hooks/useCreateTradeForm';
import type { TradeForm } from './types';
import { inputClassName, labelClassName } from './types';

/**
 * LR-586: the pool that holds a cash-collateral trade's cash.
 *
 * Mandatory for cash collateral - both here and in the API, which re-checks every
 * filter below (a booking through the API has no dropdown). Rendered only for
 * `collateral_type === 'cash'`: a non-cash trade holds no pool cash and the
 * backend rejects a pool on it.
 *
 * No auto-suggest by rate: trades with different fees legitimately share one pool
 * (product answer #12), so the trade rate says nothing about which pool is right.
 * When no pool fits, the trader is sent to Collateral Manager to create one - we
 * never create one silently (answer #9).
 */
export interface CashPoolFieldProps {
  form: TradeForm;
  isSubmitted: boolean;
  disabled?: boolean;
}

export const CashPoolField: React.FC<CashPoolFieldProps> = ({ form, isSubmitted, disabled }) => {
  const counterpartyId = useStore(
    form.store,
    (state: { values: TradeFormValues }) => state.values.counterparty_id
  );
  const settlementCurrency = useStore(
    form.store,
    (state: { values: TradeFormValues }) => state.values.settlement_currency
  );
  const securityCurrency = useStore(
    form.store,
    (state: { values: TradeFormValues }) => state.values.currency
  );
  const tradeType = useStore(
    form.store,
    (state: { values: TradeFormValues }) => state.values.trade_type
  );
  const selected = useStore(
    form.store,
    (state: { values: TradeFormValues }) => state.values.cash_pool
  );

  // GMRA and GMSLA cash may not share a pool (product answer #1). Derived from
  // the trade side through the shared helper the backend's `_agreement_type`
  // mirrors, and not hand-rolled, so a fifth trade type cannot make the dropdown
  // and the API disagree. Since LR-634 this is the only source there is: the
  // form has no `agreement` field, because the counterparty registry - which
  // used to fill it - says nothing about what a trade books under.
  const product = getAgreementType(tradeType);
  const settlement = (settlementCurrency || '').toUpperCase();
  const counterparty = counterpartyId ? Number(counterpartyId) : undefined;
  const canQuery = !!counterparty;

  const {
    data: pools = [],
    isLoading,
    isError,
  } = useCashPools(
    { counterparty, product, in_agreement_currency: true, is_active: true },
    {
      enabled: canQuery,
      // The "no pool fits" link opens Collateral Manager in a second tab, so the
      // pool the trader just created lives in a different query cache entirely.
      // Zero stale time turns the tab-switch back into a refetch (TanStack
      // refetches on window focus, but only what is stale) - a small registry
      // read against booking with a list that no longer reflects reality.
      staleTime: 0,
    }
  );

  const options = useMemo(
    () =>
      pools.map((pool) => ({
        value: pool.id,
        label: pool.rate_definition ? `${pool.name} · ${pool.rate_definition}` : pool.name,
      })),
    [pools]
  );

  // Counterparty, trade side or (GMRA) currency changed under a selection that no longer
  // qualifies -> drop it. Without this the form posts a pool the API rejects, with
  // an error pointing at a field the trader never touched. Gated on the fetch
  // having settled, so a pool is not cleared merely because the list is in flight
  // or the registry read failed.
  useEffect(() => {
    if (!selected || isLoading || isError || !canQuery) return;
    if (!pools.some((pool) => pool.id === selected)) {
      form.setFieldValue('cash_pool', null);
    }
  }, [selected, pools, isLoading, isError, canQuery, form]);

  // Remembered so a retained selection stays VISIBLE. While the list is in
  // flight or the read failed, `options` is empty, and a <select> whose value
  // matches no option falls back to displaying the placeholder - the trader
  // would read their pick as lost while the form still holds (and posts) it.
  const lastLabel = useRef<string | null>(null);
  useEffect(() => {
    const hit = options.find((option) => option.value === selected);
    if (hit) lastLabel.current = hit.label;
  }, [options, selected]);

  const selectedPool = pools.find((pool) => pool.id === selected);

  const shownOptions =
    selected && !options.some((option) => option.value === selected)
      ? [...options, { value: selected, label: lastLabel.current ?? `Pool #${selected}` }]
      : options;

  // A FAILED registry read must not read as "this counterparty has no pool":
  // duplicates are legal, so that message would walk the trader into creating a
  // second pool for one that already exists.
  const noPools = canQuery && !isLoading && !isError && options.length === 0;
  // Nothing to pick, but the control stays focusable: `disabled` would drop a
  // MANDATORY field out of the tab order, and the paragraph explaining why it
  // cannot be filled is wired through aria-describedby, which a disabled control
  // never reaches.
  const inert = !canQuery || noPools || isError;

  return (
    <form.Field
      name="cash_pool"
      validators={{
        // LR-718: the same gate as `poolApplies`, read LIVE off the form. (The render
        // condition adds `!amendMode`, where this field never mounts at all, so
        // `validateAllFields` skips it for want of an instance.)
        // A captured prop would freeze on the last mounted render - `FieldApi.mount()`
        // returns an empty cleanup, so an unmounted field stays registered and its
        // validator keeps failing a field that is no longer on screen, killing Create
        // with nothing to show for it.
        onChange: ({ value }: { value: number | null | undefined }) => {
          const { trade_type, rate_entry_mode, collateral_type } = form.state.values;
          if (collateral_type !== 'cash' || !poolsItsCash(trade_type, rate_entry_mode)) {
            return undefined;
          }
          return value ? undefined : 'Cash pool is required for cash collateral';
        },
      }}
    >
      {(field: {
        state: { value: number | null | undefined; meta: { errors: unknown[] } };
        handleChange: (value: number | null) => void;
      }) => {
        const showError = isSubmitted && field.state.meta.errors.length > 0;
        const describedBy =
          [isError && 'cash_pool-fetch-error', noPools && 'cash_pool-hint', showError && 'cash_pool-error']
            .filter(Boolean)
            .join(' ') || undefined;
        return (
          <>
            <label className={labelClassName} htmlFor="cash_pool">
              Cash Pool <span className="text-red-500">*</span>
            </label>
            <select
              id="cash_pool"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value ? Number(e.target.value) : null)}
              disabled={disabled}
              aria-disabled={inert || undefined}
              aria-required="true"
              aria-invalid={showError || undefined}
              aria-describedby={describedBy}
              className={clsx(
                inputClassName,
                (disabled || inert) && 'bg-gray-100 cursor-not-allowed'
              )}
            >
              <option value="">
                {canQuery ? 'Select Cash Pool' : 'Select counterparty first'}
              </option>
              {shownOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isError && (
              <p id="cash_pool-fetch-error" className="mt-1 text-xs text-red-600" role="alert">
                Could not load cash pools. Retry before booking - this is not
                &ldquo;no pool exists&rdquo;.
              </p>
            )}
            {/* Converted only when neither the settlement nor the security is in the pool's
                currency: the backend's `pool_conversion_pair`. */}
            {product === 'gmsla' &&
              selectedPool &&
              settlement &&
              selectedPool.currency !== settlement &&
              (securityCurrency || '').toUpperCase() !== selectedPool.currency && (
              <p className="mt-1 text-xs text-gray-500">
                Settles in {settlement}: the collateral goes into this {selectedPool.currency} pool
                at the closing rate.
              </p>
            )}
            {product === 'gmra' &&
              selectedPool &&
              securityCurrency &&
              securityCurrency.toUpperCase() !== selectedPool.currency && (
              <p className="mt-1 text-xs text-gray-500">
                Priced in {securityCurrency.toUpperCase()}: the margin call converts it into this{' '}
                {selectedPool.currency} pool at the closing rate.
              </p>
            )}
            {noPools && (
              <p id="cash_pool-hint" className="mt-1 text-xs text-gray-500">
                No {product.toUpperCase()} pool for this counterparty.{' '}
                {/* A plain new-tab anchor, NOT a router link: the Trade Entry
                    dialog is mounted outside <Outlet /> and has no dismissable
                    onClose, so navigating in place would drop the trader behind
                    a modal backdrop covering the very page they were sent to,
                    with the ticket only recoverable by discarding it. */}
                <a
                  href="/collateral"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Create one in Collateral Manager
                </a>
              </p>
            )}
            {showError && (
              <p id="cash_pool-error" className="mt-1 text-xs text-red-600">
                {String(field.state.meta.errors[0])}
              </p>
            )}
          </>
        );
      }}
    </form.Field>
  );
};
